/* eslint-disable react/jsx-props-no-spreading */
import { useSpringValue } from '@react-spring/web';
import classNames from 'classnames';
import { useCallback, useRef, useState } from 'react';

import usePage from '../../hooks/usePage';

import { useThemeClassName } from '../../contexts/ThemeContext';
import Button from '../buttons/Button';
import BonusCard from '../items/BonusCard';
import PageTitle from '../partials/PageTitle';

import styles from '../../../styles/pages/bonus-page.module.scss';

function BonusPage() {
    const { page = null, refetch: reload } = usePage();
    const { title = 'Page', bonuses } = page;
    const refScroll = useRef(null);
    const refCards = useRef([]);
    const themeClassName = useThemeClassName(styles);
    const [activeIndex, setActiveIndex] = useState(0);
    const onBonusComplete = useCallback(() => {
        reload();
    }, [reload]);

    const [scrolling, setScrolling] = useState(false);
    const scrollSpring = useSpringValue(0, {
        onRest: () => {
            setScrolling(false);
        },
        onChange: (y) => {
            // console.log(y);
            refScroll.current.scrollLeft = y;
            setScrolling(true);
        },
    });

    const onClickPill = useCallback(
        (index) => {
            setActiveIndex(index);
            scrollSpring.start(refCards.current[index].offsetLeft - 20);
        },
        [scrollSpring],
    );

    const onScroll = useCallback(
        (e) => {
            if (scrolling) {
                return;
            }
            const { scrollLeft } = e.currentTarget;
            const newIndex = refCards.current.reduce((currentIndex, ref, index) => {
                const startLeft = ref.offsetLeft - ref.offsetWidth / 3;
                return scrollLeft >= startLeft ? index : currentIndex;
            }, 0);
            if (activeIndex !== newIndex) {
                setActiveIndex(newIndex);
            }
        },
        [activeIndex, scrolling],
    );

    return (
        <div className={classNames([styles.container, themeClassName])}>
            <div className={styles.inner}>
                <PageTitle title={title} />
                <div
                    className={styles.bonuses}
                    ref={refScroll}
                    onScroll={onScroll}
                    style={{
                        scrollSnapType: scrolling ? 'none' : 'x mandatory',
                    }}
                >
                    {bonuses.map((bonus, index) => (
                        <div
                            key={`bonus-${bonus.id}`}
                            className={styles.item}
                            ref={(ref) => {
                                refCards.current[index] = ref;
                            }}
                        >
                            <BonusCard
                                key={`bonus-${bonus.id}`}
                                {...bonus}
                                onComplete={() => onBonusComplete(bonus)}
                                className={styles.bonus}
                            />
                        </div>
                    ))}
                </div>
                {bonuses.length > 1 ? (
                    <nav className={styles.pills}>
                        {bonuses.map((bonus, index) => (
                            <div
                                key={`pill-${bonus.id}`}
                                className={classNames([
                                    styles.item,
                                    {
                                        [styles.active]: index === activeIndex,
                                    },
                                ])}
                            >
                                <Button onClick={() => onClickPill(index)} className={styles.pill}>
                                    {index + 1}
                                </Button>
                            </div>
                        ))}
                    </nav>
                ) : null}
            </div>
        </div>
    );
}

export default BonusPage;
